import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { fetchProducts } from '../features/products/productsSlice';
import Header from './Header';
import { API_URL, BASE_URL, getImageUrl, normalizeList, normalizeStorageUrl } from '../config';
import { isValidYouTubeUrl, normalizeYouTubeUrl, isClothesOrGarmentsCategory, getClothesSizeOptions } from '../utils/productHelpers';
import './AddProductPage.css';

const DESCRIPTION_EDITOR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ font: [] }],
    [{ size: ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ align: [] }],
    ['blockquote', 'code-block'],
    ['link', 'image'],
    ['clean'],
  ],
};

const DESCRIPTION_EDITOR_FORMATS = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'script',
  'list', 'bullet', 'indent', 'align',
  'blockquote', 'code-block',
  'link', 'image',
];

const createLocalImage = (file, preview) => ({
  id: Date.now() + Math.random(),
  file,
  preview,
  uploadedUrl: null,
  uploading: false,
  error: null,
});
const createColorVariantRow = () => ({
  id: Date.now() + Math.random(),
  colorId: '',
  sizeIds: [],
  sizeStocks: {},
  stock: '',
  images: [],
});

const getRowSizeStock = (row, sizeId) => {
  const map = row?.sizeStocks || {};
  return map[sizeId] ?? map[String(sizeId)] ?? '';
};

const mapColorVariantPayload = (row) => {
  const sizeIds = (row.sizeIds || []).map((id) => parseInt(id, 10)).filter((id) => id > 0);
  const sizeStocks = sizeIds.map((sizeId) => ({
    sizeId,
    stock: parseInt(getRowSizeStock(row, sizeId), 10) || 0,
  }));
  return {
    colorId: parseInt(row.colorId, 10),
    sizeIds,
    sizeStocks,
    stock: sizeStocks.reduce((sum, item) => sum + item.stock, 0),
    images: row.images
      .filter((img) => img.uploadedUrl)
      .map((img) => ({ imageUrl: img.uploadedUrl })),
  };
};

const AddProductPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userRole = useSelector((state) => state.auth.userRole);
  const token = localStorage.getItem('jwtToken');
  const fileInputRef = useRef(null);
  const bannerFileInputRef = useRef(null);
  const brandImageFileInputRef = useRef(null);
  const videoThumbFileInputRef = useRef(null);

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [tertiaryCategories, setTertiaryCategories] = useState([]);
  const [quaternaryCategories, setQuaternaryCategories] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [enableBanner, setEnableBanner] = useState(false);
  const [bannerImage, setBannerImage] = useState({ file: null, preview: null, uploadedUrl: null, uploading: false, error: null });
  const [showOnHomePageBrand, setShowOnHomePageBrand] = useState(false);
  const [isTrending, setIsTrending] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [brandImage, setBrandImage] = useState({ file: null, preview: null, uploadedUrl: null, uploading: false, error: null });
  const [videoThumbnailImage, setVideoThumbnailImage] = useState({
    file: null,
    preview: null,
    uploadedUrl: null,
    uploading: false,
    error: null,
  });
  const [sizeDropdownValue, setSizeDropdownValue] = useState('');

  const [form, setForm] = useState({
    productName: '',
    description: '',
    price: '',
    originalPrice: '',
    stock: '',
    categoryId: '',
    subCategoryId: '',
    tertiaryCategoryId: '',
    quaternaryCategoryId: '',
    brandName: '',
    shortDescription: '',
    sellerId: '',
    deliveryDays: '5',
    deliveryType: 'free',
    deliveryCharge: '0',
    sellerAddress: '',
    sellerStockQuantity: '',
    colorIds: [],
    sizeIds: [],
    youtubeVideoUrl: '',
  });

  // Multiple images state: array of { id, file, preview, uploadedUrl, uploading, error }
  const [images, setImages] = useState([]);
  const [uploadError, setUploadError] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [colorVariants, setColorVariants] = useState([]);
  const [colorVariantError, setColorVariantError] = useState('');

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const catRes = await axios.get(`${API_URL}/Categories`);
        setCategories(normalizeList(catRes.data));
      } catch (err) {
        setError('Failed to load categories.');
      } finally {
        setLoading(false);
      }
    };
    fetchInitial();
  }, []);

  useEffect(() => {
    if (!form.categoryId) {
      setSubCategories([]);
      setForm(prev => ({ ...prev, subCategoryId: '', tertiaryCategoryId: '', quaternaryCategoryId: '' }));
      return;
    }
    axios.get(`${API_URL}/SubCategories`, { params: { categoryId: form.categoryId } })
      .then(res => setSubCategories(normalizeList(res.data)))
      .catch(() => setSubCategories([]));
    setForm(prev => ({ ...prev, subCategoryId: '', tertiaryCategoryId: '', quaternaryCategoryId: '' }));
  }, [form.categoryId]);

  useEffect(() => {
    if (!form.subCategoryId) {
      setTertiaryCategories([]);
      setForm(prev => ({ ...prev, tertiaryCategoryId: '', quaternaryCategoryId: '' }));
      return;
    }
    axios.get(`${API_URL}/TertiaryCategories`, { params: { subCategoryId: form.subCategoryId } })
      .then(res => setTertiaryCategories(normalizeList(res.data)))
      .catch(() => setTertiaryCategories([]));
    setForm(prev => ({ ...prev, tertiaryCategoryId: '', quaternaryCategoryId: '' }));
  }, [form.subCategoryId]);

  useEffect(() => {
    if (!form.tertiaryCategoryId) {
      setQuaternaryCategories([]);
      setForm(prev => ({ ...prev, quaternaryCategoryId: '' }));
      return;
    }
    axios.get(`${API_URL}/QuaternaryCategories`, { params: { tertiaryCategoryId: form.tertiaryCategoryId } })
      .then(res => setQuaternaryCategories(normalizeList(res.data)))
      .catch(() => setQuaternaryCategories([]));
    setForm(prev => ({ ...prev, quaternaryCategoryId: '' }));
  }, [form.tertiaryCategoryId]);

  useEffect(() => {
    const q = form.quaternaryCategoryId || form.tertiaryCategoryId || form.subCategoryId;
    if (!q || !token) {
      setSellers([]);
      return;
    }
    const params = form.quaternaryCategoryId
      ? { quaternaryCategoryId: form.quaternaryCategoryId }
      : form.tertiaryCategoryId
        ? { tertiaryCategoryId: form.tertiaryCategoryId }
        : { subCategoryId: form.subCategoryId };
    axios.get(`${API_URL}/ProductSellers/sellers`, { headers: { Authorization: `Bearer ${token}` }, params })
      .then(res => setSellers(normalizeList(res.data)))
      .catch(() => setSellers([]));
  }, [token, form.subCategoryId, form.tertiaryCategoryId, form.quaternaryCategoryId]);

  const selectedCategory = categories.find((c) => String(c.categoryId) === String(form.categoryId));
  const isClothesCategory = isClothesOrGarmentsCategory({
    categoryId: form.categoryId || selectedCategory?.categoryId,
    categoryName: selectedCategory?.categoryName,
  });
  const selectedSubCategory = subCategories.find((s) => String(s.subCategoryId) === String(form.subCategoryId));
  const {
    sizeOptions,
    preferredSizeNames,
    missingSizeNames,
    isKidsWearSubCategory,
    isMenWomenSubCategory,
  } = getClothesSizeOptions(sizes, selectedSubCategory?.subCategoryName);
  const selectedSizeOptions = sizeOptions.filter((size) => (form.sizeIds || []).includes(size.sizeId));

  useEffect(() => {
    if (!isClothesCategory) {
      setColors([]);
      setSizes([]);
      setForm(prev => ({ ...prev, colorIds: [], sizeIds: [] }));
      setColorVariants([]);
      setColorVariantError('');
      return;
    }
    Promise.all([
      axios.get(`${API_URL}/Colors`).then(res => normalizeList(res.data)),
      axios.get(`${API_URL}/Sizes`).then(res => normalizeList(res.data)),
    ])
      .then(([colorsList, sizesList]) => {
        setColors(Array.isArray(colorsList) ? colorsList : []);
        setSizes(Array.isArray(sizesList) ? sizesList : []);
      })
      .catch(() => {
        setColors([]);
        setSizes([]);
      });
  }, [isClothesCategory]);

  useEffect(() => {
    setSizeDropdownValue('');
  }, [form.subCategoryId]);

  useEffect(() => {
    if (!isClothesCategory) {
      return;
    }

    const allowedSizeIds = new Set(sizeOptions.map((size) => size.sizeId));
    setForm((prev) => {
      const nextSizeIds = (prev.sizeIds || []).filter((sizeId) => allowedSizeIds.has(sizeId));
      return nextSizeIds.length === (prev.sizeIds || []).length
        ? prev
        : { ...prev, sizeIds: nextSizeIds };
    });
  }, [isClothesCategory, sizeOptions]);

  useEffect(() => {
    if (!isLoggedIn || userRole !== 'Admin') {
      navigate('/login');
    }
  }, [isLoggedIn, userRole, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const updateColorVariantRow = (rowId, updater) => {
    setColorVariants((prev) => prev.map((row) => (
      row.id === rowId ? updater(row) : row
    )));
  };

  const addColorVariantRow = () => {
    setColorVariants((prev) => [...prev, createColorVariantRow()]);
    setColorVariantError('');
    setError('');
  };

  const removeColorVariantRow = (rowId) => {
    setColorVariants((prev) => prev.filter((row) => row.id !== rowId));
    setColorVariantError('');
    setError('');
  };

  const handleColorVariantColorChange = (rowId, colorId) => {
    updateColorVariantRow(rowId, (row) => ({
      ...row,
      colorId,
    }));
    setColorVariantError('');
    setError('');
  };

  const addSizeToColorVariant = (rowId, sizeId) => {
    if (!sizeId) return;
    updateColorVariantRow(rowId, (row) => {
      const current = Array.isArray(row.sizeIds) ? row.sizeIds : [];
      if (current.includes(sizeId)) return row;
      return {
        ...row,
        sizeIds: [...current, sizeId],
        sizeStocks: { ...(row.sizeStocks || {}), [sizeId]: getRowSizeStock(row, sizeId) },
      };
    });
    setColorVariantError('');
    setError('');
  };

  const removeSizeFromColorVariant = (rowId, sizeId) => {
    updateColorVariantRow(rowId, (row) => {
      const nextStocks = { ...(row.sizeStocks || {}) };
      delete nextStocks[sizeId];
      delete nextStocks[String(sizeId)];
      return {
        ...row,
        sizeIds: (row.sizeIds || []).filter((id) => Number(id) !== Number(sizeId)),
        sizeStocks: nextStocks,
      };
    });
  };

  const updateColorSizeStock = (rowId, sizeId, stock) => {
    updateColorVariantRow(rowId, (row) => ({
      ...row,
      sizeStocks: { ...(row.sizeStocks || {}), [sizeId]: stock },
    }));
  };

  const addFilesToColorVariant = (rowId, files) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;

    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      if (!allowedTypes.includes(file.type)) {
        errors.push(`${file.name}: Invalid file type.`);
        return;
      }
      if (file.size > maxSize) {
        errors.push(`${file.name}: File too large (max 5MB).`);
        return;
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      setColorVariantError(errors.join(' '));
    } else {
      setColorVariantError('');
    }

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateColorVariantRow(rowId, (row) => ({
          ...row,
          images: [...row.images, createLocalImage(file, reader.result)],
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleColorVariantDrop = (rowId, e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      addFilesToColorVariant(rowId, files);
    }
  };

  const handleColorVariantDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const uploadColorVariantImage = async (rowId, imageId) => {
    const row = colorVariants.find((variantRow) => variantRow.id === rowId);
    const image = row?.images.find((img) => img.id === imageId);
    if (!image || !image.file || image.uploadedUrl) return;

    updateColorVariantRow(rowId, (variantRow) => ({
      ...variantRow,
      images: variantRow.images.map((img) => (
        img.id === imageId ? { ...img, uploading: true, error: null } : img
      )),
    }));

    const formData = new FormData();
    formData.append('file', image.file);

    try {
      const response = await axios.post(
        `${API_URL}/ImageUpload/product`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const uploadedUrl = `${BASE_URL}${response.data.imageUrl}`;
      updateColorVariantRow(rowId, (variantRow) => ({
        ...variantRow,
        images: variantRow.images.map((img) => (
          img.id === imageId ? { ...img, uploadedUrl, uploading: false } : img
        )),
      }));
    } catch (err) {
      const errorMsg =
        err.response?.status === 401
          ? 'Please log in as Admin.'
          : err.response?.data || 'Upload failed.';
      updateColorVariantRow(rowId, (variantRow) => ({
        ...variantRow,
        images: variantRow.images.map((img) => (
          img.id === imageId
            ? { ...img, uploading: false, error: typeof errorMsg === 'string' ? errorMsg : 'Upload failed.' }
            : img
        )),
      }));
    }
  };

  const uploadAllColorVariantImages = async (rowId) => {
    const row = colorVariants.find((variantRow) => variantRow.id === rowId);
    const pendingImages = row?.images.filter((img) => img.file && !img.uploadedUrl && !img.uploading);
    if (!pendingImages?.length) return;

    for (const img of pendingImages) {
      await uploadColorVariantImage(rowId, img.id);
    }
  };

  const removeColorVariantImage = (rowId, imageId) => {
    updateColorVariantRow(rowId, (row) => ({
      ...row,
      images: row.images.filter((img) => img.id !== imageId),
    }));
  };

  // For color-variant galleries: clicking a thumbnail makes it the first image in that row.
  const makeColorVariantImageMain = (rowId, imageId) => {
    updateColorVariantRow(rowId, (row) => {
      const idx = row.images.findIndex((img) => img.id === imageId);
      if (idx <= 0) return row;
      const target = row.images[idx];
      return { ...row, images: [target, ...row.images.filter((img) => img.id !== imageId)] };
    });
  };

  const handleSizeDropdownChange = (e) => {
    const value = e.target.value;
    setSizeDropdownValue(value);

    const sizeId = parseInt(value, 10);
    if (!sizeId) {
      return;
    }

    setForm((prev) => {
      const existingSizeIds = Array.isArray(prev.sizeIds) ? prev.sizeIds : [];
      if (existingSizeIds.includes(sizeId)) {
        return prev;
      }

      return { ...prev, sizeIds: [...existingSizeIds, sizeId] };
    });

    setSizeDropdownValue('');
    setError('');
  };

  const removeSelectedSize = (sizeId) => {
    setForm((prev) => ({
      ...prev,
      sizeIds: (prev.sizeIds || []).filter((id) => id !== sizeId),
    }));
    setError('');
  };

  const handleFilesSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      addFiles(files);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      addFiles(files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const addFiles = (files) => {
    setUploadError('');
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    const maxImages = 10;

    if (images.length + files.length > maxImages) {
      setUploadError(`Maximum ${maxImages} images allowed.`);
      return;
    }

    const errors = [];

    files.forEach((file) => {
      if (!allowedTypes.includes(file.type)) {
        errors.push(`${file.name}: Invalid file type.`);
        return;
      }
      if (file.size > maxSize) {
        errors.push(`${file.name}: File too large (max 5MB).`);
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        const newImage = {
          id: Date.now() + Math.random(),
          file,
          preview: reader.result,
          uploadedUrl: null,
          uploading: false,
          error: null,
        };
        setImages((prev) => [...prev, newImage]);
      };
      reader.readAsDataURL(file);
    });

    if (errors.length > 0) {
      setUploadError(errors.join(' '));
    }
  };

  const uploadImage = async (imageId) => {
    const imageIndex = images.findIndex((img) => img.id === imageId);
    if (imageIndex === -1) return;

    const image = images[imageIndex];
    if (!image.file || image.uploadedUrl) return;

    // Set uploading state
    setImages((prev) =>
      prev.map((img) =>
        img.id === imageId ? { ...img, uploading: true, error: null } : img
      )
    );

    const formData = new FormData();
    formData.append('file', image.file);

    try {
      const response = await axios.post(
        `${API_URL}/ImageUpload/product`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const uploadedUrl = `${BASE_URL}${response.data.imageUrl}`;
      setImages((prev) =>
        prev.map((img) =>
          img.id === imageId ? { ...img, uploadedUrl, uploading: false } : img
        )
      );
    } catch (err) {
      const errorMsg =
        err.response?.status === 401
          ? 'Please log in as Admin.'
          : err.response?.data || 'Upload failed.';
      setImages((prev) =>
        prev.map((img) =>
          img.id === imageId
            ? { ...img, uploading: false, error: typeof errorMsg === 'string' ? errorMsg : 'Upload failed.' }
            : img
        )
      );
    }
  };

  const uploadAllImages = async () => {
    const imagesToUpload = images.filter((img) => img.file && !img.uploadedUrl && !img.uploading);
    for (const img of imagesToUpload) {
      await uploadImage(img.id);
    }
  };

  const uploadBannerImage = async () => {
    if (!enableBanner) return true;
    if (!bannerImage.file && !bannerImage.uploadedUrl) {
      setError('Please choose a banner image (or turn off banner).');
      return false;
    }
    if (!bannerImage.file || bannerImage.uploadedUrl) return true;

    setBannerImage((prev) => ({ ...prev, uploading: true, error: null }));
    const formData = new FormData();
    formData.append('file', bannerImage.file);
    try {
      const response = await axios.post(
        `${API_URL}/ImageUpload/product`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const uploadedUrl = normalizeStorageUrl(response.data.imageUrl);
      setBannerImage((prev) => ({
        ...prev,
        uploadedUrl,
        preview: getImageUrl(uploadedUrl),
        uploading: false,
      }));
      return true;
    } catch (err) {
      const errorMsg =
        err.response?.status === 401
          ? 'Please log in as Admin.'
          : err.response?.data || 'Banner upload failed.';
      setBannerImage((prev) => ({ ...prev, uploading: false, error: typeof errorMsg === 'string' ? errorMsg : 'Banner upload failed.' }));
      return false;
    }
  };

  const uploadBrandImage = async () => {
    if (!brandImage.file || brandImage.uploadedUrl) return true;

    setBrandImage((prev) => ({ ...prev, uploading: true, error: null }));
    const formData = new FormData();
    formData.append('file', brandImage.file);

    try {
      const response = await axios.post(
        `${API_URL}/ImageUpload/product`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const uploadedUrl = `${BASE_URL}${response.data.imageUrl}`;
      setBrandImage((prev) => ({ ...prev, uploadedUrl, uploading: false }));
      return true;
    } catch (err) {
      const errorMsg =
        err.response?.status === 401
          ? 'Please log in as Admin.'
          : err.response?.data || 'Brand image upload failed.';
      setBrandImage((prev) => ({
        ...prev,
        uploading: false,
        error: typeof errorMsg === 'string' ? errorMsg : 'Brand image upload failed.',
      }));
      return false;
    }
  };

  const setVideoThumbnailFromFile = (file) => {
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    const maxSize = 5 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      setVideoThumbnailImage({
        file: null,
        preview: null,
        uploadedUrl: null,
        uploading: false,
        error: 'Please select a JPG, PNG, WebP, or GIF image.',
      });
      return;
    }
    if (file.size > maxSize) {
      setVideoThumbnailImage({
        file: null,
        preview: null,
        uploadedUrl: null,
        uploading: false,
        error: 'Image size must be under 5MB.',
      });
      return;
    }
    const preview = URL.createObjectURL(file);
    setVideoThumbnailImage((prev) => {
      if (prev.preview && prev.preview.startsWith('blob:')) {
        URL.revokeObjectURL(prev.preview);
      }
      return {
        file,
        preview,
        uploadedUrl: null,
        uploading: false,
        error: null,
      };
    });
  };

  const handleVideoThumbnailDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    setVideoThumbnailFromFile(file);
  };

  const handleVideoThumbnailDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const uploadVideoThumbnail = async () => {
    if (!videoThumbnailImage.file && !videoThumbnailImage.uploadedUrl) return true;
    if (!videoThumbnailImage.file || videoThumbnailImage.uploadedUrl) return true;

    setVideoThumbnailImage((prev) => ({ ...prev, uploading: true, error: null }));
    const formData = new FormData();
    formData.append('file', videoThumbnailImage.file);
    try {
      const response = await axios.post(
        `${API_URL}/ImageUpload/product`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const uploadedUrl = normalizeStorageUrl(response.data.imageUrl);
      setVideoThumbnailImage((prev) => ({
        ...prev,
        uploadedUrl,
        preview: getImageUrl(uploadedUrl),
        uploading: false,
      }));
      return true;
    } catch (err) {
      const errorMsg =
        err.response?.status === 401
          ? 'Please log in as Admin.'
          : err.response?.data || 'Video image upload failed.';
      setVideoThumbnailImage((prev) => ({
        ...prev,
        uploading: false,
        error: typeof errorMsg === 'string' ? errorMsg : 'Video image upload failed.',
      }));
      return false;
    }
  };

  const removeImage = (imageId) => {
    setImages((prev) => prev.filter((img) => img.id !== imageId));
  };

  // Amazon-style gallery behavior: clicking a thumbnail makes it the main image.
  // "Main" is always index 0 to match the backend save logic.
  const makeImageMain = (imageId) => {
    setImages((prev) => {
      const idx = prev.findIndex((img) => img.id === imageId);
      if (idx <= 0) return prev;
      const target = prev[idx];
      return [target, ...prev.filter((img) => img.id !== imageId)];
    });
  };

  // Drag and drop reordering
  const handleImageDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleImageDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    setImages((prev) => {
      const newImages = [...prev];
      const [draggedItem] = newImages.splice(draggedIndex, 1);
      newImages.splice(index, 0, draggedItem);
      return newImages;
    });
    setDraggedIndex(index);
  };

  const handleImageDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const productName = form.productName?.trim();
    const subCategoryId = form.subCategoryId ? parseInt(form.subCategoryId, 10) : 0;
    const price = parseFloat(form.price);
    const originalPrice = form.originalPrice !== '' && form.originalPrice != null ? parseFloat(form.originalPrice) : null;
    const stock = parseInt(form.stock, 10);

    if (!productName) {
      setError('Product name is required.');
      return;
    }
    if ((showOnHomePageBrand || brandImage.file || brandImage.uploadedUrl) && !form.brandName?.trim()) {
      setError('Please enter a brand name before saving brand image details.');
      return;
    }
    if (!form.categoryId) {
      setError('Please select a category.');
      return;
    }
    if (!subCategoryId) {
      setError('Please select a subcategory.');
      return;
    }
    if (isNaN(price) || price < 0) {
      setError('Please enter a valid price.');
      return;
    }
    if (originalPrice != null && (isNaN(originalPrice) || originalPrice < 0)) {
      setError('Original price must be a valid positive number.');
      return;
    }
    if (originalPrice != null && originalPrice <= price) {
      setError('Original price must be greater than selling price to show discount.');
      return;
    }
    if (!isClothesCategory && (isNaN(stock) || stock < 0)) {
      setError('Please enter a valid stock quantity.');
      return;
    }
    if (isClothesCategory) {
      if (!colorVariants.length) {
        setError('Please add at least one color variant for clothes products.');
        return;
      }

      const invalidColorVariant = colorVariants.find((row) => !row.colorId);
      if (invalidColorVariant) {
        setError('Please select a color for each color variant.');
        return;
      }

      if (sizeOptions.length > 0 && colorVariants.some((row) => !(row.sizeIds || []).length)) {
        setError('Please select at least one size for each color.');
        return;
      }

      const duplicateColorIds = new Set();
      for (const row of colorVariants) {
        if (duplicateColorIds.has(row.colorId)) {
          setError('Each color variant should be added only once.');
          return;
        }
        duplicateColorIds.add(row.colorId);
      }

      const emptyImageRow = colorVariants.find((row) => !row.images.some((img) => img.uploadedUrl));
      if (emptyImageRow) {
        setError('Please upload at least one image for every color variant.');
        return;
      }

      const invalidSizeStock = colorVariants.find((row) =>
        (row.sizeIds || []).some((sizeId) => {
          const qty = parseInt(getRowSizeStock(row, sizeId), 10);
          return Number.isNaN(qty) || qty < 0;
        })
      );
      if (invalidSizeStock) {
        setError('Please enter stock for each size of every color.');
        return;
      }
    }

    if (!form.sellerId) {
      setError('Please select a seller before saving the product.');
      return;
    }

    // Check if there are images that need uploading
    const unuploadedImages = images.filter((img) => img.file && !img.uploadedUrl && !img.error);
    if (unuploadedImages.length > 0) {
      setError('Please upload all images before saving.');
      return;
    }

    const unuploadedColorVariantImages = colorVariants.flatMap((row) =>
      row.images.filter((img) => img.file && !img.uploadedUrl && !img.error)
    );
    if (unuploadedColorVariantImages.length > 0) {
      setError('Please upload all color variant images before saving.');
      return;
    }

    const youtubeUrl = form.youtubeVideoUrl?.trim() || '';
    const hasYoutubeUrl = youtubeUrl.length > 0;
    const hasVideoThumbnail = Boolean(videoThumbnailImage.uploadedUrl || videoThumbnailImage.file);

    if (hasYoutubeUrl && !isValidYouTubeUrl(youtubeUrl)) {
      setError('Please enter a valid YouTube video URL.');
      return;
    }
    if (hasYoutubeUrl && !videoThumbnailImage.uploadedUrl) {
      setError('Please upload the video thumbnail image before saving.');
      return;
    }
    if (hasVideoThumbnail && !hasYoutubeUrl) {
      setError('Please enter the YouTube video URL for the uploaded video image.');
      return;
    }
    if (videoThumbnailImage.file && !videoThumbnailImage.uploadedUrl) {
      setError('Please upload the video thumbnail image before saving.');
      return;
    }

    if (enableBanner && bannerImage.file && !bannerImage.uploadedUrl) {
      setError('Please upload the banner image before saving.');
      return;
    }
    if (brandImage.file && !brandImage.uploadedUrl) {
      setError('Please upload the brand image before saving.');
      return;
    }
    if (showOnHomePageBrand && !brandImage.uploadedUrl) {
      setError('Please upload a brand image before enabling it for the home page.');
      return;
    }
    if (form.sellerId && form.deliveryType === 'paid' && !(parseFloat(form.deliveryCharge) > 0)) {
      setError('Enter a delivery charge greater than 0, or choose Free delivery.');
      return;
    }

    // Get uploaded image URLs
    const uploadedImages = images
      .filter((img) => img.uploadedUrl)
      .map((img) => ({ imageUrl: img.uploadedUrl }));
    const uploadedColorVariants = colorVariants
      .filter((row) => row.colorId)
      .map(mapColorVariantPayload)
      .filter((row) => row.images.length > 0);
    const allSizeIds = [...new Set(uploadedColorVariants.flatMap((row) => row.sizeIds || []))];
    const resolvedStock = isClothesCategory
      ? uploadedColorVariants.reduce((sum, row) => sum + (Number(row.stock) || 0), 0)
      : stock;

    const sellerStockQuantity = form.sellerStockQuantity === ''
      ? resolvedStock
      : parseInt(form.sellerStockQuantity, 10);

    setSaving(true);
    try {
      // Create product
      await axios.post(
        `${API_URL}/Products`,
        {
          productName,
          description: form.description?.trim() || null,
          price,
          originalPrice,
          imageUrl: uploadedImages.length > 0
            ? uploadedImages[0].imageUrl
            : (uploadedColorVariants[0]?.images[0]?.imageUrl || null),
          stock: resolvedStock,
          subCategoryId,
          tertiaryCategoryId: form.tertiaryCategoryId ? parseInt(form.tertiaryCategoryId, 10) : null,
          quaternaryCategoryId: form.quaternaryCategoryId ? parseInt(form.quaternaryCategoryId, 10) : null,
          brandName: form.brandName?.trim() || null,
          brandImageUrl: brandImage.uploadedUrl || null,
          showOnHomePageBrand,
          isTrending,
          isFeatured,
          shortDescription: form.shortDescription?.trim() || null,
          videoUrl: hasYoutubeUrl ? normalizeYouTubeUrl(youtubeUrl) : null,
          videoThumbnailUrl: videoThumbnailImage.uploadedUrl
            ? normalizeStorageUrl(videoThumbnailImage.uploadedUrl)
            : null,
          images: uploadedImages,
          enableBanner,
          bannerImageUrl: enableBanner ? (normalizeStorageUrl(bannerImage.uploadedUrl) || null) : null,
          sellerId: form.sellerId ? parseInt(form.sellerId, 10) : null,
          deliveryDays: parseInt(form.deliveryDays, 10) || 5,
          deliveryCharge: form.deliveryType === 'paid' ? (parseFloat(form.deliveryCharge) || 0) : 0,
          sellerAddress: form.sellerAddress?.trim() || null,
          stockQuantity: Number.isNaN(sellerStockQuantity) ? stock : sellerStockQuantity,
          colorIds: isClothesCategory ? uploadedColorVariants.map((row) => row.colorId) : undefined,
          colorVariants: isClothesCategory ? uploadedColorVariants : undefined,
          sizeIds: isClothesCategory && allSizeIds.length ? allSizeIds : undefined,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccess(true);
      dispatch(fetchProducts(null));
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Please log in as Admin.');
      } else if (err.response?.data) {
        setError(typeof err.response.data === 'string' ? err.response.data : 'Failed to add product.');
      } else {
        setError('Failed to add product.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isLoggedIn || userRole !== 'Admin') {
    return null;
  }

  if (loading) {
    return (
      <div className="add-product-page">
        <div className="add-product-loading">Loading...</div>
      </div>
    );
  }

  const hasUnuploadedImages = images.some((img) => img.file && !img.uploadedUrl && !img.uploading && !img.error);
  const isUploading = images.some((img) => img.uploading);
  const hasPendingColorVariantUploads = colorVariants.some((row) =>
    row.images.some((img) => img.file && !img.uploadedUrl && !img.uploading && !img.error)
  );
  const isColorVariantUploading = colorVariants.some((row) =>
    row.images.some((img) => img.uploading)
  );

  return (
    <div className="add-product-page">
      <Header />

      <div className="add-product-container">
        <button type="button" className="back-link" onClick={() => navigate(-1)}>← Back</button>
        <div className="add-product-main">
          <h1>Add Product</h1>
          <p className="add-product-desc">Fill in the fields below to add a new product. You can upload multiple images from different angles.</p>

          {success && (
            <div className="success-message">Product added successfully. Redirecting...</div>
          )}

          <form onSubmit={handleSubmit} className="add-product-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="productName">Product Name *</label>
                <input
                  id="productName"
                  name="productName"
                  type="text"
                  value={form.productName}
                  onChange={handleChange}
                  required
                  placeholder="e.g. Men's Jeans"
                  maxLength={200}
                />
              </div>
            </div>

            <div className="form-group category-cascade">
              <label>Category *</label>
              <div className="cascade-row">
                <select
                  id="categoryId"
                  name="categoryId"
                  value={form.categoryId}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select category</option>
                  {categories.map((c) => (
                    <option key={c.categoryId} value={c.categoryId}>{c.categoryName}</option>
                  ))}
                </select>
                <select
                  id="subCategoryId"
                  name="subCategoryId"
                  value={form.subCategoryId}
                  onChange={handleChange}
                  required
                >
                  <option value="">Subcategory</option>
                  {subCategories.map((s) => (
                    <option key={s.subCategoryId} value={s.subCategoryId}>{s.subCategoryName}</option>
                  ))}
                </select>
                <select
                  id="tertiaryCategoryId"
                  name="tertiaryCategoryId"
                  value={form.tertiaryCategoryId}
                  onChange={handleChange}
                >
                  <option value="">Tertiary (optional)</option>
                  {tertiaryCategories.map((t) => (
                    <option key={t.tertiaryCategoryId} value={t.tertiaryCategoryId}>{t.tertiaryCategoryName}</option>
                  ))}
                </select>
                <select
                  id="quaternaryCategoryId"
                  name="quaternaryCategoryId"
                  value={form.quaternaryCategoryId}
                  onChange={handleChange}
                >
                  <option value="">Quaternary (optional)</option>
                  {quaternaryCategories.map((q) => (
                    <option key={q.quaternaryCategoryId} value={q.quaternaryCategoryId}>{q.quaternaryCategoryName}</option>
                  ))}
                </select>
              </div>
              <p className="category-hint">Select category and subcategory at minimum. Sellers listed below are those allowed to sell in the selected category.</p>
            </div>

            <div className="form-group description-editor-group">
              <label htmlFor="description">Description</label>
              <ReactQuill
                id="description"
                theme="snow"
                value={form.description}
                onChange={(value) => {
                  setForm((prev) => ({ ...prev, description: value }));
                  setError('');
                }}
                placeholder="Product description (use formatting for headings, lists, links, etc.)"
                modules={DESCRIPTION_EDITOR_MODULES}
                formats={DESCRIPTION_EDITOR_FORMATS}
                className="description-rich-editor"
              />
            </div>

            <div className="form-group">
              <label htmlFor="shortDescription">Short Description</label>
              <input
                id="shortDescription"
                name="shortDescription"
                type="text"
                value={form.shortDescription}
                onChange={handleChange}
                placeholder="Brief summary (e.g. for About this item)"
                maxLength={500}
              />
            </div>

            <div className="form-row pricing-fields">
              <div className="form-group">
                <label htmlFor="price">Price *</label>
                <input
                  id="price"
                  name="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={handleChange}
                  required
                  placeholder="0.00"
                />
              </div>
              <div className="form-group">
                <label htmlFor="originalPrice">MRP (optional)</label>
                <input
                  id="originalPrice"
                  name="originalPrice"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.originalPrice}
                  onChange={handleChange}
                  placeholder="Leave empty if no discount"
                />
              </div>
              {!isClothesCategory && (
              <div className="form-group">
                <label htmlFor="stock">Stock *</label>
                <input
                  id="stock"
                  name="stock"
                  type="number"
                  min="0"
                  value={form.stock}
                  onChange={handleChange}
                  required
                  placeholder="0"
                />
              </div>
              )}
            </div>

            {/* Multiple Images Upload Section */}
            <div className="form-group image-upload-section">
              <label>Product Images (up to 10)</label>
              <p className="image-hint">
                Click any thumbnail to set main image. Drag thumbnails to reorder.
              </p>
              
              {/* Amazon-style image gallery */}
              {images.length > 0 && (
                <div className="admin-product-image-gallery">
                  <div className="admin-thumbnail-strip">
                    {images.map((img, index) => (
                      <div
                        key={img.id}
                        className={`admin-thumbnail ${index === 0 ? 'active' : ''} ${draggedIndex === index ? 'dragging' : ''}`}
                        draggable
                        onDragStart={(e) => handleImageDragStart(e, index)}
                        onDragOver={(e) => handleImageDragOver(e, index)}
                        onDragEnd={handleImageDragEnd}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (draggedIndex !== null) return;
                          makeImageMain(img.id);
                        }}
                      >
                        <img src={img.preview} alt={`Product thumbnail ${index + 1}`} />
                        {index === 0 && <span className="main-badge">Main</span>}
                        {img.uploading && (
                          <div className="image-overlay">
                            <span className="uploading-indicator">Uploading...</span>
                          </div>
                        )}
                        {img.uploadedUrl && <div className="image-uploaded-check">✓</div>}
                        {img.error && (
                          <div className="image-error-badge" title={img.error}>!</div>
                        )}
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeImage(img.id);
                          }}
                          title="Remove image"
                          disabled={img.uploading}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>

                  {images[0] && (
                    <div className="admin-main-image-wrap">
                      <img
                        src={images[0].preview}
                        alt="Main product"
                        className="admin-main-product-image"
                      />
                      {images[0].uploading && (
                        <div className="image-overlay">
                          <span className="uploading-indicator">Uploading...</span>
                        </div>
                      )}
                      {images[0].uploadedUrl && (
                        <div className="image-uploaded-check">✓</div>
                      )}
                      {images[0].error && (
                        <div className="image-error-badge" title={images[0].error}>!</div>
                      )}
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={() => removeImage(images[0].id)}
                        title="Remove main image"
                        disabled={images[0].uploading}
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Drop zone for adding more images */}
              <div 
                className="image-drop-zone multi"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="drop-zone-content">
                  <span className="drop-icon">📷</span>
                  <span>Drag & drop images here, or click to select</span>
                  <span className="file-hint">JPG, PNG, GIF, WebP (max 5MB each, up to 10 images)</span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleFilesSelect}
                  className="file-input-hidden"
                  multiple
                />
              </div>

              {/* Upload all button */}
              {hasUnuploadedImages && (
                <div className="upload-actions">
                  <button 
                    type="button" 
                    className="upload-btn"
                    onClick={uploadAllImages}
                    disabled={isUploading}
                  >
                    {isUploading ? 'Uploading...' : `Upload ${images.filter(img => img.file && !img.uploadedUrl && !img.error).length} Image(s)`}
                  </button>
                </div>
              )}

              {uploadError && (
                <div className="upload-error">{uploadError}</div>
              )}

              {images.length > 0 && images.every((img) => img.uploadedUrl) && (
                <div className="upload-success">
                  ✓ All {images.length} image(s) uploaded successfully
                </div>
              )}
            </div>

            <div className="form-group image-upload-section">
              <label htmlFor="youtubeVideoUrl">Product Video — YouTube (optional)</label>
              <p className="field-hint">
                Upload a thumbnail image and link a YouTube URL. Customers will see the image on the home page and open the video on YouTube when they click it.
              </p>
              <input
                id="youtubeVideoUrl"
                name="youtubeVideoUrl"
                type="url"
                className="video-youtube-url-input"
                value={form.youtubeVideoUrl}
                onChange={handleChange}
                placeholder="https://www.youtube.com/watch?v=..."
              />
              {(videoThumbnailImage.preview || videoThumbnailImage.uploadedUrl) && (
                <div className="video-preview video-thumbnail-preview">
                  <img
                    src={videoThumbnailImage.preview || getImageUrl(videoThumbnailImage.uploadedUrl)}
                    alt="Video thumbnail preview"
                  />
                </div>
              )}
              {videoThumbnailImage.uploadedUrl && (
                <div className="upload-success">✓ Video thumbnail uploaded</div>
              )}
              {videoThumbnailImage.error && (
                <div className="upload-error">{videoThumbnailImage.error}</div>
              )}
              <div
                className="image-drop-zone multi"
                onClick={() => videoThumbFileInputRef.current?.click()}
                onDrop={handleVideoThumbnailDrop}
                onDragOver={handleVideoThumbnailDragOver}
              >
                <div className="drop-zone-content">
                  <span className="drop-icon">🖼️</span>
                  <span>Drag & drop video thumbnail here, or click to select</span>
                  <span className="file-hint">JPG, PNG, WebP, GIF (max 5MB)</span>
                </div>
                <input
                  ref={videoThumbFileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                  className="file-input-hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    setVideoThumbnailFromFile(file);
                    e.target.value = '';
                  }}
                />
              </div>
              <div className="upload-actions">
                <button
                  type="button"
                  className="upload-btn"
                  onClick={uploadVideoThumbnail}
                  disabled={
                    videoThumbnailImage.uploading
                    || !videoThumbnailImage.file
                    || !!videoThumbnailImage.uploadedUrl
                  }
                >
                  {videoThumbnailImage.uploading ? 'Uploading...' : 'Upload video image'}
                </button>
                {(videoThumbnailImage.preview || videoThumbnailImage.uploadedUrl) && (
                  <button
                    type="button"
                    className="upload-btn"
                    onClick={() => {
                      if (videoThumbnailImage.preview && videoThumbnailImage.preview.startsWith('blob:')) {
                        URL.revokeObjectURL(videoThumbnailImage.preview);
                      }
                      setVideoThumbnailImage({
                        file: null,
                        preview: null,
                        uploadedUrl: null,
                        uploading: false,
                        error: null,
                      });
                    }}
                  >
                    Remove video image
                  </button>
                )}
              </div>
            </div>

            {/* Product banner for home slider */}
            <div className="form-group image-upload-section">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={enableBanner}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setEnableBanner(checked);
                    if (!checked) {
                      setBannerImage({ file: null, preview: null, uploadedUrl: null, uploading: false, error: null });
                      if (bannerFileInputRef.current) bannerFileInputRef.current.value = '';
                    }
                  }}
                />
                Show this product in banner slider (upload banner image)
              </label>

              {enableBanner && (
                <div className="banner-upload-box">
                  {(bannerImage.preview || bannerImage.uploadedUrl) && (
                    <div className="banner-preview">
                      <img
                        src={bannerImage.preview || getImageUrl(bannerImage.uploadedUrl)}
                        alt="Banner preview"
                      />
                    </div>
                  )}

                  {bannerImage.uploadedUrl && (
                    <div className="upload-success">✓ Banner uploaded</div>
                  )}

                  {bannerImage.error && (
                    <div className="upload-error">{bannerImage.error}</div>
                  )}

                  <div className="upload-actions">
                    <input
                      ref={bannerFileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="file-input-hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (!file.type.startsWith('image/')) {
                          setBannerImage({ file: null, preview: null, uploadedUrl: null, uploading: false, error: 'Please select a valid image.' });
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = () => {
                          setBannerImage({ file, preview: reader.result, uploadedUrl: null, uploading: false, error: null });
                        };
                        reader.readAsDataURL(file);
                      }}
                    />

                    <button
                      type="button"
                      className="upload-btn"
                      onClick={() => bannerFileInputRef.current?.click()}
                      disabled={bannerImage.uploading}
                    >
                      Choose banner image
                    </button>

                    <button
                      type="button"
                      className="upload-btn"
                      onClick={uploadBannerImage}
                      disabled={bannerImage.uploading || !bannerImage.file || !!bannerImage.uploadedUrl}
                    >
                      {bannerImage.uploading ? 'Uploading...' : 'Upload banner'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="brandName">Brand Name</label>
              <input
                id="brandName"
                name="brandName"
                type="text"
                value={form.brandName}
                onChange={handleChange}
                placeholder="e.g. Levi's"
                maxLength={200}
              />
            </div>

            <div className="form-group">
              <label>Home page product tags</label>
              <p className="field-hint">Tagged products appear in the matching home page section. You can select both.</p>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isFeatured}
                  onChange={(e) => setIsFeatured(e.target.checked)}
                />
                Featured product
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isTrending}
                  onChange={(e) => setIsTrending(e.target.checked)}
                />
                Trending product
              </label>
            </div>

            <div className="form-group image-upload-section">
              <label>Brand Image</label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={showOnHomePageBrand}
                  onChange={(e) => setShowOnHomePageBrand(e.target.checked)}
                />
                Show this brand in home page "Our Brands" section
              </label>

              <div className="banner-upload-box">
                {brandImage.preview && (
                  <div className="brand-image-preview">
                    <img src={brandImage.preview} alt="Brand preview" />
                  </div>
                )}

                {brandImage.uploadedUrl && (
                  <div className="upload-success">✓ Brand image uploaded</div>
                )}

                {brandImage.error && (
                  <div className="upload-error">{brandImage.error}</div>
                )}

                <div className="upload-actions">
                  <input
                    ref={brandImageFileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="file-input-hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (!file.type.startsWith('image/')) {
                        setBrandImage({ file: null, preview: null, uploadedUrl: null, uploading: false, error: 'Please select a valid image.' });
                        return;
                      }
                      const reader = new FileReader();
                      reader.onload = () => {
                        setBrandImage({ file, preview: reader.result, uploadedUrl: null, uploading: false, error: null });
                      };
                      reader.readAsDataURL(file);
                    }}
                  />

                  <button
                    type="button"
                    className="upload-btn"
                    onClick={() => brandImageFileInputRef.current?.click()}
                    disabled={brandImage.uploading}
                  >
                    Choose brand image
                  </button>

                  <button
                    type="button"
                    className="upload-btn"
                    onClick={uploadBrandImage}
                    disabled={brandImage.uploading || !brandImage.file || !!brandImage.uploadedUrl}
                  >
                    {brandImage.uploading ? 'Uploading...' : 'Upload brand image'}
                  </button>
                </div>
              </div>
            </div>

            {/* Sizes & Colors for Clothes / Garments */}
            {isClothesCategory && (
              <div className="form-group clothes-attributes">
                <label>Sizes &amp; Colors (for this product)</label>
                <p className="clothes-attributes-hint">
                  Add a row for each colour. On every row pick the colour, the sizes it is sold in, stock for each size, and photos.
                  New size or color names are added under Admin → Sizes &amp; Colors.
                </p>
                <div className="attributes-row">
                  <div className="attribute-group">
                    <span className="attribute-label">Colors, sizes &amp; stock</span>
                    {colors.length === 0 && (
                      <p className="clothes-selection-note">
                        No colors found. Add them first from Admin → Sizes &amp; Colors, then click + Add color option.
                      </p>
                    )}
                    {colorVariantError && <div className="upload-error">{colorVariantError}</div>}
                    <div className="color-variant-builder">
                      {colorVariants.map((variantRow) => {
                        const pendingUploads = variantRow.images.filter((img) => img.file && !img.uploadedUrl && !img.error).length;
                        return (
                          <div key={variantRow.id} className="color-variant-card-builder">
                            <div className="color-variant-header">
                              <select
                                value={variantRow.colorId}
                                onChange={(e) => handleColorVariantColorChange(variantRow.id, e.target.value)}
                                className="color-variant-select"
                              >
                                <option value="">Select color</option>
                                {colors
                                  .filter((color) => !colorVariants.some((row) => row.id !== variantRow.id && row.colorId === String(color.colorId)))
                                  .map((color) => (
                                    <option key={color.colorId} value={color.colorId}>
                                      {color.colorName}
                                    </option>
                                  ))}
                              </select>
                              <label className="color-stock-label">
                                Size
                                <select
                                  value=""
                                  onChange={(e) => {
                                    const sizeId = parseInt(e.target.value, 10);
                                    if (sizeId) addSizeToColorVariant(variantRow.id, sizeId);
                                  }}
                                  className="color-size-select"
                                >
                                  <option value="">Select size</option>
                                  {sizeOptions.map((size) => (
                                    <option
                                      key={size.sizeId}
                                      value={size.sizeId}
                                      disabled={(variantRow.sizeIds || []).some((id) => Number(id) === Number(size.sizeId))}
                                    >
                                      {size.sizeName}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <button
                                type="button"
                                className="remove-color-variant-btn"
                                onClick={() => removeColorVariantRow(variantRow.id)}
                              >
                                Remove color
                              </button>
                            </div>
                            <div className="selected-size-chips">
                              {(variantRow.sizeIds || []).length === 0 ? (
                                <span className="selected-size-empty">No sizes selected for this color yet.</span>
                              ) : (
                                (variantRow.sizeIds || []).map((sizeId) => {
                                  const size = sizeOptions.find((s) => Number(s.sizeId) === Number(sizeId));
                                  if (!size) return null;
                                  return (
                                    <div key={size.sizeId} className="size-stock-item">
                                      <span className="size-stock-name">{size.sizeName}</span>
                                      <input
                                        type="number"
                                        min="0"
                                        className="size-stock-input"
                                        placeholder="Qty"
                                        value={getRowSizeStock(variantRow, size.sizeId)}
                                        onChange={(e) => updateColorSizeStock(variantRow.id, size.sizeId, e.target.value)}
                                      />
                                      <button
                                        type="button"
                                        className="size-stock-remove"
                                        onClick={() => removeSizeFromColorVariant(variantRow.id, size.sizeId)}
                                        title="Remove size"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  );
                                })
                              )}
                            </div>

                            {variantRow.images.length > 0 && (
                              <div className="color-variant-image-gallery">
                                <div className="color-variant-thumbnail-strip">
                                  {variantRow.images.map((img, idx) => (
                                    <div
                                      key={img.id}
                                      className={`color-variant-thumbnail ${idx === 0 ? 'active' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        makeColorVariantImageMain(variantRow.id, img.id);
                                      }}
                                    >
                                      <img src={img.preview} alt="Color variant thumbnail" />
                                      {idx === 0 && <span className="main-badge">Main</span>}
                                      {img.uploading && (
                                        <div className="image-overlay">
                                          <span className="uploading-indicator">Uploading...</span>
                                        </div>
                                      )}
                                      {img.uploadedUrl && <div className="image-uploaded-check">✓</div>}
                                      {img.error && (
                                        <div className="image-error-badge" title={img.error}>!</div>
                                      )}
                                      <button
                                        type="button"
                                        className="remove-image-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeColorVariantImage(variantRow.id, img.id);
                                        }}
                                        disabled={img.uploading}
                                        title="Remove image"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ))}
                                </div>

                                <div className="color-variant-main-image-wrap">
                                  {variantRow.images[0] && (
                                    <>
                                      <img
                                        src={variantRow.images[0].preview}
                                        alt="Main color variant"
                                        className="color-variant-main-image"
                                      />
                                      {variantRow.images[0].uploading && (
                                        <div className="image-overlay">
                                          <span className="uploading-indicator">Uploading...</span>
                                        </div>
                                      )}
                                      {variantRow.images[0].uploadedUrl && (
                                        <div className="image-uploaded-check">✓</div>
                                      )}
                                      {variantRow.images[0].error && (
                                        <div
                                          className="image-error-badge"
                                          title={variantRow.images[0].error}
                                        >
                                          !
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            )}

                            <label
                              className="color-variant-upload-box image-drop-zone multi"
                              onDrop={(e) => handleColorVariantDrop(variantRow.id, e)}
                              onDragOver={handleColorVariantDragOver}
                            >
                              <div className="drop-zone-content">
                                <span className="drop-icon">📷</span>
                                <span>Drag &amp; drop images here, or click to select</span>
                                <span className="file-hint">JPG, PNG, GIF, WebP (max 5MB each, multiple images allowed)</span>
                              </div>
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/gif,image/webp"
                                multiple
                                className="file-input-hidden"
                                onChange={(e) => {
                                  const files = Array.from(e.target.files || []);
                                  if (files.length) {
                                    addFilesToColorVariant(variantRow.id, files);
                                  }
                                  e.target.value = '';
                                }}
                              />
                            </label>

                            {pendingUploads > 0 && (
                              <div className="upload-actions">
                                <button
                                  type="button"
                                  className="upload-btn"
                                  onClick={() => uploadAllColorVariantImages(variantRow.id)}
                                  disabled={isColorVariantUploading}
                                >
                                  {isColorVariantUploading ? 'Uploading...' : `Upload ${pendingUploads} image(s)`}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        className="add-color-variant-btn"
                        onClick={addColorVariantRow}
                      >
                        + Add color option
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {error && <div className="error-message">{error}</div>}

            {/* Seller (required – must select before saving) */}
            <div className="form-group seller-section">
              <label>Seller *</label>
              <p className="seller-hint">Select a seller for this product. Only sellers allowed in the selected category are listed. You must select a seller before saving.</p>
              {form.subCategoryId && sellers.length === 0 && (
                <p className="seller-empty-hint">No sellers in this category. Assign categories to sellers when adding a seller.</p>
              )}
              <div className="seller-single-row">
                <div className="seller-field">
                  <select
                    name="sellerId"
                    value={form.sellerId}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select seller</option>
                    {sellers.map(s => (
                      <option key={s.sellerId} value={s.sellerId}>
                        {s.sellerName} - {s.sellerCode || s.sellerId}
                      </option>
                    ))}
                  </select>
                </div>
                {form.sellerId && (
                  <>
                    <div className="seller-field">
                      <label>Delivery days</label>
                      <input
                        type="number"
                        min="1"
                        name="deliveryDays"
                        value={form.deliveryDays}
                        onChange={handleChange}
                        placeholder="5"
                      />
                    </div>
                    <div className="seller-field delivery-option-field">
                      <label>Delivery</label>
                      <div className="delivery-option-group">
                        <label className="delivery-option">
                          <input
                            type="radio"
                            name="deliveryType"
                            value="free"
                            checked={form.deliveryType === 'free'}
                            onChange={(e) => setForm((prev) => ({ ...prev, deliveryType: e.target.value, deliveryCharge: '0' }))}
                          />
                          Free delivery
                        </label>
                        <label className="delivery-option">
                          <input
                            type="radio"
                            name="deliveryType"
                            value="paid"
                            checked={form.deliveryType === 'paid'}
                            onChange={(e) => setForm((prev) => ({ ...prev, deliveryType: e.target.value }))}
                          />
                          Delivery charges applied
                        </label>
                      </div>
                    </div>
                    {form.deliveryType === 'paid' && (
                      <div className="seller-field">
                        <label>Delivery charge (₹)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          name="deliveryCharge"
                          value={form.deliveryCharge}
                          onChange={handleChange}
                          placeholder="e.g. 40"
                          required
                        />
                      </div>
                    )}
                    <div className="seller-field">
                      <label>Seller stock</label>
                      <input
                        type="number"
                        min="0"
                        name="sellerStockQuantity"
                        value={form.sellerStockQuantity}
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                  </>
                )}
              </div>
              {form.sellerId && (
                <div className="seller-row">
                  <div className="seller-field full-width">
                    <label>Seller address</label>
                    <input
                      type="text"
                      name="sellerAddress"
                      value={form.sellerAddress}
                      onChange={handleChange}
                      placeholder="e.g. Mumbai, Maharashtra, India"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="button" className="cancel-button" onClick={() => navigate(-1)} disabled={saving || isUploading}>
                Cancel
              </button>
              <button type="submit" className="save-button" disabled={saving || isUploading || isColorVariantUploading || hasUnuploadedImages || hasPendingColorVariantUploads || !form.sellerId}>
                {saving ? 'Saving...' : 'Save Product'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddProductPage;
